import React, { memo, useRef, useState } from "react";
import groupBy from "lodash/groupBy";
import clsx from "clsx";
import { shallowEqual } from "react-redux";
import slider from "../../../../../../../../shared-components/css-modules/lexus/utils/DraggableSlider.module.scss";
import styles from "./Wheels.module.scss";
import Container from "../../../../../../../../shared-components/css-modules/lexus/atoms/Container";
import Text from "../../../../../../../../shared-components/css-modules/lexus/atoms/Text";
import toggle from "../../../../../../../../shared-components/css-modules/lexus/organisms/Switch.module.scss";
import Wheel from "./Wheel";
import { useSlider } from "../../../../../../../../../../common-deprecated/features/apheleia/hooks/useSlider";
import { CarConfigConfigureSectionsLbx } from "../../../../../../../utils/types/SectionTypesLbx";
import SubSectionWrapper from "../SubSectionWrapper";
import type { CarConfigDispatchType } from "../../../../../../../redux/store";
import { useCarConfigSelector } from "../../../../../../../redux/store";
import type { AtmosphereWheeldriveType } from "../../../../../../../utils/types/AtmosphereType";
import type { SectionWrapperPropagationProps } from "../../../../../common/section-wrapper/SectionWrapper";
import { selectCurrentAtmosphereGroupIndex } from "../../../../../../../redux/slices/CarConfigAtmosphereSlice";
import { find } from "../../../../../../../../../../common-deprecated/utils";
import CarDataConsolidator from "../../../../../../../../../shared-logic/utils/classes/CarDataConsolidator";
import WinterWheelFitting from "../../../../../configure/sections/wheels/WinterWheelFitting";
import WinterWheel from "./WinterWheel";
import { useAphLbxAtmospheres } from "../../../../../../../hooks/lbx/useAphLbxAtmospheres";
import { toggleWinterWheelTab } from "../../../../../../../redux/slices/CarConfigNavigationSlice";
import { CarConfigLabel, useCarConfigLabel } from "../../../../../../../utils/labelUtils";
import { useDispatch } from "../../../../../../../../../../shared-logic/redux/store";

const Wheels: React.FC<SectionWrapperPropagationProps<CarConfigConfigureSectionsLbx>> = ({
    sectionRefs,
}): JSX.Element => {
    const dispatch = useDispatch<CarConfigDispatchType>();

    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const anyItemRef = useRef<HTMLDivElement | null>(null);

    const winterWheelsActive = useCarConfigSelector((state) => state.carConfigNavigation.winterWheelTabActive);

    // Having local state so the WinterWheelFitting section keeps working when the installation gets removed
    // Need to define it here considering when selecting a winter wheel, it also needs to know about the installation (or the last value of the WinterWheelFitting installation checkbox)
    const [includeInstallation, setIncludeInstallation] = useState(true);

    const atmospheres = useAphLbxAtmospheres();
    const selectedAtmosphereGroupIndex = useCarConfigSelector(selectCurrentAtmosphereGroupIndex, shallowEqual);
    const winterWheelsItems = useCarConfigSelector((state) =>
        CarDataConsolidator.getWinterWheels(state.items.winterWheels, state.basket.colour, state.basket.upholstery),
    );
    const currentWinterWheel = useCarConfigSelector((state) => state.basket.winterWheel);
    const navigationLabel = useCarConfigLabel("carConfigA11yNavWinterWheel");

    // It's possible that the installation gets removed (ex.: in the overview) hence we prioritize the winterwheel check over the local state
    const installTheWheels = currentWinterWheel ? currentWinterWheel.installTheWheels : includeInstallation;

    const groupedAtmospheres = groupBy(atmospheres, "category");

    const groupedWheels = Object.entries(groupedAtmospheres).reduce(
        (categoryObj, [categoryName, categoryAtmospheres], index) => {
            const wheels: AtmosphereWheeldriveType["wheels"] = [];
            // If the base category is not selected, so when a higher atmosphere is selected, don't show the wheels for the lowest category (base atmosphere).
            if (selectedAtmosphereGroupIndex > 0 ? index !== 0 : true)
                categoryAtmospheres.forEach((atmosphere) => {
                    atmosphere.wheeldrives.forEach((wheeldrive) => {
                        wheeldrive.wheels.forEach((wheel) => {
                            if (!find(wheel.id, winterWheelsItems) && !find(wheel.id, wheels)) wheels.push(wheel);
                        });
                    });
                });

            return { ...categoryObj, [categoryName]: wheels };
        },
        {} as { [index: string]: AtmosphereWheeldriveType["wheels"] },
    );

    const groupedWinterWheels = Object.entries(groupedAtmospheres).reduce(
        (categoryObj, [categoryName, categoryAtmospheres], index) => {
            const atmosphereWinterWheels: AtmosphereWheeldriveType["winterWheels"] = [];
            // If the base category is not selected, so when a higher atmosphere is selected, don't show the wheels for the lowest category (base atmosphere).
            if (selectedAtmosphereGroupIndex > 0 ? index !== 0 : true)
                categoryAtmospheres.forEach((atmosphere) => {
                    atmosphere.wheeldrives.forEach((wheeldrive) => {
                        wheeldrive.winterWheels.forEach((winterWheel) => {
                            if (!find(winterWheel.id, atmosphereWinterWheels)) atmosphereWinterWheels.push(winterWheel);
                        });
                    });
                });

            return { ...categoryObj, [categoryName]: atmosphereWinterWheels };
        },
        {} as { [index: string]: AtmosphereWheeldriveType["winterWheels"] },
    );
    const { elementProps, isDragging } = useSlider(wrapperRef, anyItemRef);

    return (
        <SubSectionWrapper
            sectionId={CarConfigConfigureSectionsLbx.AtmosphereWheels}
            sectionRef={sectionRefs.get(CarConfigConfigureSectionsLbx.AtmosphereWheels)}
        >
            <Container>
                <header>
                    <div className={styles.intro}>
                        <Text tag="h3" variant="apheleia-h4">
                            <CarConfigLabel label="carConfigLbxAlloys" />
                        </Text>
                        {winterWheelsItems.length > 0 ? (
                            <div className={styles.toggle}>
                                <nav className={toggle.switch} aria-label={navigationLabel}>
                                    <input
                                        id="winter-wheels-toggle-on"
                                        className={`${toggle.toggle} ${toggle.left}`}
                                        name="toggleWinterWheels"
                                        value="false"
                                        type="radio"
                                        onChange={() => {
                                            // TODO: tracking
                                            dispatch(toggleWinterWheelTab());
                                        }}
                                        defaultChecked={!winterWheelsActive}
                                    />
                                    <label htmlFor="winter-wheels-toggle-on" className={toggle.option}>
                                        <CarConfigLabel label="carConfigWheelsToggle" />
                                    </label>
                                    <input
                                        id="winter-wheels-toggle-off"
                                        className={`${toggle.toggle} ${toggle.right}`}
                                        name="toggleWinterWheels"
                                        value="true"
                                        type="radio"
                                        onChange={() => {
                                            // TODO: tracking
                                            dispatch(toggleWinterWheelTab());
                                        }}
                                        defaultChecked={winterWheelsActive}
                                    />
                                    <label htmlFor="winter-wheels-toggle-off" className={toggle.option}>
                                        <CarConfigLabel label="carConfigWinterWheelsToggle" />
                                    </label>
                                </nav>
                            </div>
                        ) : (
                            <Text tag="p" variant="apheleia-body">
                                <CarConfigLabel label="carConfigLbxAlloysIntro" />
                            </Text>
                        )}
                    </div>
                </header>
            </Container>
            {!winterWheelsActive ? (
                <div
                    className={clsx(slider.wrapper, styles.slider, isDragging && slider.dragging)}
                    ref={wrapperRef}
                    {...elementProps}
                >
                    {Object.entries(groupedWheels).map(
                        ([categoryName, wheels]) =>
                            wheels.length > 0 && (
                                <div className={styles.group} key={categoryName}>
                                    {categoryName && (
                                        <Text tag="div" variant="apheleia-body-small" className={styles.legend}>
                                            {categoryName}
                                        </Text>
                                    )}
                                    {wheels.map((wheel, index) => (
                                        <Wheel
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={`${categoryName}${wheel.id}${index}`}
                                            wheel={wheel}
                                            index={index}
                                        />
                                    ))}
                                </div>
                            ),
                    )}
                </div>
            ) : (
                <>
                    <Container>
                        <div className={styles.winterfitting}>
                            <WinterWheelFitting
                                winterWheel={currentWinterWheel}
                                installTheWheels={installTheWheels}
                                setIncludeInstallation={setIncludeInstallation}
                            />
                        </div>
                    </Container>
                    <div
                        className={clsx(
                            slider.wrapper,
                            styles.slider,
                            styles.winterwheels,
                            isDragging && slider.dragging,
                        )}
                        ref={wrapperRef}
                        {...elementProps}
                    >
                        {Object.entries(groupedWinterWheels).map(
                            ([categoryName, winterWheels]) =>
                                winterWheels.length > 0 && (
                                    <div className={styles.group} key={categoryName}>
                                        {categoryName && (
                                            <Text tag="div" variant="apheleia-body-small" className={styles.legend}>
                                                {categoryName}
                                            </Text>
                                        )}
                                        {winterWheels.map((wheel, index) => (
                                            <WinterWheel
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={`${categoryName}${wheel.id}${index}`}
                                                wheel={wheel}
                                                index={index}
                                                categoryName={categoryName}
                                            />
                                        ))}
                                    </div>
                                ),
                        )}
                    </div>
                </>
            )}
        </SubSectionWrapper>
    );
};

export default memo(Wheels);
